import type { CSSProperties } from "react";
import { ActivityIcon, Button } from "@/ui";
import { shortDate } from "@/lib/date";
import type { ActivityView, GoalView } from "./dashboard";
import { goalProgressPct } from "./goalProgress";
import { MetricList } from "./MetricList";
import { PlanGantt } from "./PlanGantt";

const ICON_SIZE = 22;
const NO_PURPOSE = "No purpose set.";
const NO_GOALS = "No goals yet.";
const BY_TASKS_HINT = "Tracked by tasks";

type ActivityCardProps = ActivityView & {
  onDelete: (id: string) => void;
};

/** One activity as a dashboard tile: identity, purpose, and its goals. */
export function ActivityCard({ activity, goals, metrics, onDelete }: ActivityCardProps) {
  const { shortName, purpose, icon, colorHex } = activity.fields;
  // The activity's colour drives its icon tile, progress bars, and Gantt bars.
  const tint = { "--card-tint": colorHex } as CSSProperties;
  // A Gantt needs room: a card carrying one spans two grid columns.
  const hasGantt = goals.some((view) => view.plans.length > 0);

  return (
    <li className={hasGantt ? "card is-wide" : "card"} style={tint}>
      <header className="card-head">
        <span className="card-icon">
          <ActivityIcon name={icon} size={ICON_SIZE} />
        </span>
        <div className="card-heading">
          <h3 className="card-title">{shortName}</h3>
          <p className={purpose ? "card-purpose" : "card-purpose is-empty"}>
            {purpose || NO_PURPOSE}
          </p>
        </div>
        <Button
          type="button"
          variant="danger"
          size="sm"
          className="card-delete"
          aria-label={`Delete ${shortName}`}
          onClick={() => onDelete(activity.id)}
        >
          Delete
        </Button>
      </header>

      {/* Metrics measured on the activity itself, not on any one goal. */}
      {metrics.length > 0 && (
        <section className="card-section">
          <h4 className="card-section-title">
            Metrics <span className="section-count">{metrics.length}</span>
          </h4>
          <MetricList metrics={metrics} />
        </section>
      )}

      <section className="card-section">
        <h4 className="card-section-title">
          Goals <span className="section-count">{goals.length}</span>
        </h4>
        {goals.length === 0 ? (
          <p className="card-hint">{NO_GOALS}</p>
        ) : (
          <ul className="goal-items">
            {goals.map((view) => (
              <GoalItem key={view.goal.id} {...view} />
            ))}
          </ul>
        )}
      </section>
    </li>
  );
}

/** A goal: title, deadline, progress, its own metrics, and its plans as Gantt charts. */
function GoalItem({ goal, plans, metrics }: GoalView) {
  const { title, startAt, deadlineAt } = goal.fields;
  const pct = goalProgressPct(goal.fields);

  return (
    <li className="goal-item">
      <div className="goal-item-head">
        <span className="goal-title">{title}</span>
        <span className="goal-meta">
          {pct === null ? BY_TASKS_HINT : `${pct}%`} · due {shortDate(deadlineAt)}
        </span>
      </div>
      {pct !== null && (
        <div
          className="progress"
          role="progressbar"
          aria-valuenow={pct}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`${title} progress`}
        >
          <div className="progress-value" style={{ width: `${pct}%` }} />
        </div>
      )}
      <p className="goal-start">Started {shortDate(startAt)}</p>
      {/* Metrics measured on this goal rather than on the activity as a whole. */}
      <MetricList metrics={metrics} />
      {plans.map((view) => (
        <PlanGantt key={view.plan.id} goal={goal.fields} {...view} />
      ))}
    </li>
  );
}
